// The Routines screen's two tabs — **Yours** and **Recommended for you** — and the one rule that files a
// routine under one of them.
//
// A connect installs a connector kit's whole catalogue, the recommended automations paused. Those are
// OFFERS: the screen shows them on their own tab with a blue "Recommended" pill and the switch off, and
// the moment the owner turns one on it moves to Yours and stays there — through every later pause too.
// The rule itself lives with the status logic (`routineIsRecommended`), because the pill and the tab must
// never disagree about what a routine is.
//
// The selected tab is the caller's state (the URL on web, screen state on the phones); this module only
// knows how to read it and how to split the list. Mirrored 1:1 by `RoutineTabs.swift` and
// `RoutineTabs.kt`; all three unit-test the same fixtures.

import { routineIsRecommended, type StatusableRoutine } from './routine-status';

export type RoutineTab = 'yours' | 'recommended';

/** Reading order of the strip. `yours` leads: it is what the screen opens on. */
export const ROUTINE_TABS: readonly RoutineTab[] = ['yours', 'recommended'];

export const DEFAULT_ROUTINE_TAB: RoutineTab = 'yours';

export const ROUTINE_TAB_LABEL_KEY: Record<RoutineTab, string> = {
  yours: 'routines.tab.yours',
  recommended: 'routines.tab.recommended',
};

/** The same strip on the redesigned category page (Figma 21055:246799), which shortens the second tab. */
export const CATEGORY_ROUTINE_TAB_LABEL_KEY: Record<RoutineTab, string> = {
  ...ROUTINE_TAB_LABEL_KEY,
  recommended: 'categoryView.routines.recommendedTab',
};

/** What an EMPTY tab says — different words, because the two absences have different remedies. */
export const ROUTINE_TAB_EMPTY_KEY: Record<RoutineTab, string> = {
  yours: 'routines.tab.yours.empty',
  recommended: 'routines.tab.recommended.empty',
};

/** Which tab a routine belongs on. */
export function routineTabOf(routine: StatusableRoutine): RoutineTab {
  return routineIsRecommended(routine) ? 'recommended' : 'yours';
}

/** The routines on one tab, in the order given. */
export function routinesInTab<R extends StatusableRoutine>(routines: readonly R[], tab: RoutineTab): R[] {
  return routines.filter((routine) => routineTabOf(routine) === tab);
}

/**
 * Read a tab off untrusted input (`?tab=` on web). Anything that is not exactly the other tab is the
 * default, so a mistyped or stale link opens the screen rather than an empty one.
 */
export function parseRoutineTab(raw: unknown): RoutineTab {
  return raw === 'recommended' ? 'recommended' : DEFAULT_ROUTINE_TAB;
}
